import { Injectable, output } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

type CacheOptions = {
  forceHttp?: boolean;
  cacheKey?: string;
};

@Injectable()
export abstract class HttpWithCacheService {
  private cache: Record<string, any> = {};
  private readonly cacheKeys = ['default'];
  cacheUpdated$ = new Subject<boolean>();

  protected constructor(cacheKeys?: string[]) {
    this.cacheKeys = cacheKeys ?? this.cacheKeys;
  }

  useCache(request: Observable<any>, options?: CacheOptions): Observable<any> {
    const cacheKey = options?.cacheKey ?? 'default';
    if (cacheKey && !this.cacheKeys?.includes(cacheKey)) {
      throw new Error('Invalid cache key');
    }
    const cachedData = this.cache[cacheKey];
    if (!options?.forceHttp && !!cachedData) {
      console.debug(`${this.constructor.name}: loading data from cache`);
    }

    return !!cachedData && !options?.forceHttp
      ? of(cachedData)
      : request.pipe(tap((data) => (this.cache[cacheKey] = data)));
  }

  updateCache<T = any>(cacheFn: (cachedData: T) => T, cacheKey = 'default') {
    if (cacheKey && !this.cacheKeys?.includes(cacheKey)) {
      throw new Error('Invalid cache key');
    }
    this.cache[cacheKey] = JSON.parse(
      JSON.stringify(cacheFn(this.cache[cacheKey])),
    );
    this.cacheUpdated$.next(true);
  }
}
